import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ListChecks, Flag } from "lucide-react";

/**
 * Compact at-a-glance breakdown summary for any scope.
 *   <BreakdownChip scope="project" id={projectId} />
 *   <BreakdownChip scope="assessment" id={assessmentId} />
 *
 * Shows: progress bar + "Tasks N/M" + "Milestones N/M".
 * Falls back to "No tasks yet" when empty.
 */
export default function BreakdownChip({ scope, id, dense = false, testidSuffix }) {
    const [bd, setBd] = useState(null);
    useEffect(() => {
        let alive = true;
        api.get(`/breakdown?scope=${scope}&id=${encodeURIComponent(id)}`)
            .then(({ data }) => { if (alive) setBd(data); })
            .catch(() => { if (alive) setBd({ progress: 0, tasks_total: 0, tasks_completed: 0, milestones_total: 0, milestones_completed: 0 }); });
        return () => { alive = false; };
    }, [scope, id]);

    if (!bd) {
        return <div className="h-4" />;
    }
    const empty = bd.tasks_total === 0 && bd.milestones_total === 0;
    const pct = bd.progress || 0;
    const tid = `breakdown-chip-${testidSuffix || id}`;

    if (empty) {
        return (
            <div className="text-[11px] text-[var(--text-ink)]/45 italic" data-testid={tid}>
                No tasks yet — open to add a breakdown.
            </div>
        );
    }
    return (
        <div className="space-y-1" data-testid={tid}>
            <div className="h-1.5 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                <div className="h-full brand-gradient transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className={`flex items-center gap-2 ${dense ? "text-[10px]" : "text-[11px]"} text-[var(--text-ink)]/65`}>
                <span className="tabular-nums font-medium" data-testid={`${tid}-progress`}>{pct}%</span>
                <span className="inline-flex items-center gap-0.5" data-testid={`${tid}-tasks`}>
                    <ListChecks size={11} className="opacity-65" />
                    {bd.tasks_completed}/{bd.tasks_total}
                </span>
                {bd.milestones_total > 0 && (
                    <span className="inline-flex items-center gap-0.5" data-testid={`${tid}-milestones`}>
                        <Flag size={11} className="opacity-65" />
                        {bd.milestones_completed}/{bd.milestones_total}
                    </span>
                )}
            </div>
        </div>
    );
}
